import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Sparkles,
  Wallet,
  Target,
  Chrome,
  BarChart3,
  Bot,
  ArrowRight,
  Check,
  ShieldCheck,
  Zap,
  TrendingUp,
  Lock,
  Smile,
  Coins,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CreditCardVisual } from '@/components/cards/CreditCardVisual';
import { ThemeToggle } from '@/components/shell/ThemeToggle';
import { BrandLogo, BrandMark } from '@/components/brand/BrandLogo';
import { CARDS } from '@/data/mockData';

const FEATURES = [
  {
    icon: Sparkles,
    title: 'Best card every time',
    desc: 'Real-time recommendations based on merchant, category, caps, and milestones.',
  },
  {
    icon: TrendingUp,
    title: 'Reward leakage detection',
    desc: 'Catch missed rewards and learn where you’re losing value each month.',
  },
  {
    icon: Target,
    title: 'Milestone tracker',
    desc: 'Know exactly how close you are to bonus points, vouchers, and tier upgrades.',
  },
  {
    icon: Chrome,
    title: 'Chrome extension',
    desc: 'See the best card right inside Amazon, Flipkart, Swiggy, MMT — before you pay.',
  },
  {
    icon: BarChart3,
    title: 'Deep analytics',
    desc: 'Spend by category, rewards trends, cashback vs miles — all in one view.',
  },
  {
    icon: Bot,
    title: 'AI assistant',
    desc: '“Which card for insurance?” “Am I wasting rewards?” — get instant answers.',
  },
];

const STEPS = [
  { n: '01', t: 'Add your cards', d: 'Pick from a pre-loaded library of Indian credit cards.' },
  { n: '02', t: 'Log spends', d: 'Manually or via the Chrome extension — we handle the math.' },
  { n: '03', t: 'Maximize rewards', d: 'Get smarter every month with insights, alerts, and milestone nudges.' },
];

// "Why us" — concrete differentiators rendered as a real section so the
// header link actually has content to scroll to.
const WHY_US = [
  {
    icon: Coins,
    title: 'Deterministic math, not guesses',
    desc: 'We model every rule, cap, and milestone exactly as the issuer does — no AI hallucinations, no inflated estimates.',
  },
  {
    icon: ShieldCheck,
    title: 'India-first card catalog',
    desc: 'Curated for HDFC, Axis, ICICI, SBI, Amex, IndusInd and more — with portal accelerators, tiers, and observed routing intelligence.',
  },
  {
    icon: Lock,
    title: 'Privacy by design',
    desc: 'We never store card numbers. Your transactions live in your own account and can be deleted at any time.',
  },
  {
    icon: Smile,
    title: 'Built by Indian power users',
    desc: 'Every routing hint and DSL rule is curated by people who actually swipe — not a black-box algorithm.',
  },
];

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Top nav */}
      <header className="sticky top-0 z-30 bg-[hsl(var(--background))]/85 backdrop-blur border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="landing-logo-link">
            <BrandLogo height={52} testId="landing-brand" />
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm text-foreground/80">
            <a href="#features" className="hover:text-foreground" data-testid="landing-nav-features">Features</a>
            <a href="#how" className="hover:text-foreground" data-testid="landing-nav-how">How it works</a>
            <a href="#why-us" className="hover:text-foreground" data-testid="landing-nav-why-us">Why us</a>
          </nav>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button variant="ghost" onClick={() => navigate('/login')} data-testid="landing-login-button">
              Sign in
            </Button>
            <Button onClick={() => navigate('/signup')} data-testid="landing-primary-cta" className="gap-1.5">
              Get started
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-radial-light dark:hero-radial-dark pointer-events-none" />
        <div className="absolute inset-x-0 -bottom-40 h-[420px] dotted-grid opacity-50 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24 lg:pt-24 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.24 }}
              >
                <Badge
                  variant="outline"
                  className="gap-1.5 border-primary/30 bg-primary/5 text-primary px-3 py-1 rounded-full"
                >
                  <Zap className="h-3 w-3" />
                  AI-powered • India-first • Privacy-safe
                </Badge>
              </motion.div>
              <motion.h1
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.04 }}
                className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]"
              >
                The smartest way to{' '}
                <span className="text-primary">spend your credit cards.</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.08 }}
                className="mt-5 text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed"
              >
                CardAssist Online tells you which card to swipe for every transaction — factoring in caps,
                milestones, and AI insights. Built for India’s top cards: HDFC, Axis, ICICI, SBI, Amex.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.12 }}
                className="mt-7 flex flex-wrap items-center gap-3"
              >
                <Button
                  size="lg"
                  data-testid="landing-hero-cta"
                  onClick={() => navigate('/signup')}
                  className="gap-2 h-12 px-6 text-base"
                >
                  Start optimizing
                  <ArrowRight className="h-4 w-4" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  data-testid="landing-secondary-cta"
                  onClick={() => navigate('/dashboard')}
                  className="gap-2 h-12 px-6 text-base"
                >
                  See live demo
                </Button>
              </motion.div>

              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-500" /> Free during beta</span>
                <span className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /> No card numbers stored</span>
                <span className="flex items-center gap-1.5"><Wallet className="h-3.5 w-3.5 text-emerald-500" /> Works with 50+ Indian cards</span>
              </div>
            </div>

            {/* Hero card stack */}
            <div className="lg:col-span-5">
              <div className="relative h-[420px] sm:h-[460px]">
                <motion.div
                  initial={{ opacity: 0, y: 20, rotate: -8 }}
                  animate={{ opacity: 1, y: 0, rotate: -8 }}
                  transition={{ duration: 0.4 }}
                  className="absolute top-10 left-0 w-[80%]"
                >
                  <CreditCardVisual card={CARDS[2]} size="md" />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20, rotate: -2 }}
                  animate={{ opacity: 1, y: 0, rotate: -2 }}
                  transition={{ duration: 0.4, delay: 0.08 }}
                  className="absolute top-24 left-10 w-[82%]"
                >
                  <CreditCardVisual card={CARDS[1]} size="md" />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20, rotate: 4 }}
                  animate={{ opacity: 1, y: 0, rotate: 4 }}
                  transition={{ duration: 0.4, delay: 0.16 }}
                  className="absolute top-40 left-4 w-[88%]"
                >
                  <CreditCardVisual card={CARDS[0]} size="md" />
                </motion.div>

                {/* Floating recommendation chip */}
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.3 }}
                  className="absolute bottom-6 right-0 glass rounded-2xl p-4 max-w-[280px]"
                >
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Sparkles className="h-3.5 w-3.5 text-primary" />
                    Best for Swiggy · ₹1,280
                  </div>
                  <div className="mt-1 font-display text-base font-semibold">HDFC Infinia</div>
                  <div className="mt-1 text-xs text-emerald-500">+42 RP · 3.3% effective</div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-16 lg:py-24 bg-[hsl(var(--surface-2))] border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.18em] text-primary font-medium">Features</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              Built like a fintech-grade rewards brain.
            </h2>
            <p className="mt-3 text-muted-foreground">
              We model your real reward structures — caps, milestones, accelerators, exclusions —
              and recommend the right card with full reasoning.
            </p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.24, delay: i * 0.04 }}
                >
                  <Card className="p-6 h-full hover:-translate-y-[2px] hover:shadow-card transition-shadow">
                    <div className="h-10 w-10 rounded-xl bg-primary/10 border border-primary/20 grid place-items-center">
                      <Icon className="h-[18px] w-[18px] text-primary" />
                    </div>
                    <h3 className="mt-4 font-display text-lg font-semibold tracking-tight">{f.title}</h3>
                    <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-primary font-medium">How it works</div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
                Three steps to a smarter wallet.
              </h2>
            </div>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-4 sm:gap-5">
            {STEPS.map((s) => (
              <Card key={s.n} className="p-6 relative overflow-hidden">
                <div className="font-mono-num text-xs text-muted-foreground">{s.n}</div>
                <h3 className="mt-3 font-display text-xl font-semibold tracking-tight">{s.t}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{s.d}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why us */}
      <section id="why-us" className="py-16 lg:py-24 bg-[hsl(var(--surface-2))] border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.18em] text-primary font-medium">Why us</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              Trusted by people who actually maximize their cards.
            </h2>
            <p className="mt-3 text-muted-foreground">
              CardAssist Online is the only India-first reward engine that explains exactly
              why a card is recommended, every single time.
            </p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {WHY_US.map((w, i) => {
              const Icon = w.icon;
              return (
                <motion.div
                  key={w.title}
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.24, delay: i * 0.04 }}
                >
                  <Card className="p-6 h-full">
                    <div className="h-10 w-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 grid place-items-center">
                      <Icon className="h-[18px] w-[18px] text-emerald-500" />
                    </div>
                    <h3 className="mt-4 font-display text-base font-semibold tracking-tight">{w.title}</h3>
                    <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{w.desc}</p>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Band */}
      <section id="proof" className="py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="relative overflow-hidden border-border">
            <div className="absolute inset-0 hero-radial-light dark:hero-radial-dark" />
            <div className="relative p-8 sm:p-12 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
              <div>
                <h3 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
                  Add your cards in 2 minutes. Earn more by next month.
                </h3>
                <p className="mt-2 text-muted-foreground max-w-xl">
                  Free during beta. No card numbers needed. Set up takes less than 120 seconds.
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <Button size="lg" onClick={() => navigate('/signup')} data-testid="landing-cta-band-signup" className="h-12 px-6 gap-2">
                  Start free
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-x-3 gap-y-1">
            <div className="flex items-center gap-2">
              <BrandMark size={22} />
              <span data-testid="footer-product-name">© {new Date().getFullYear()} CardAssist Online</span>
            </div>
            <span className="hidden sm:inline text-muted-foreground/40">·</span>
            <span data-testid="footer-legal-entity" className="text-[12px]">NK Advisory</span>
            <span className="hidden sm:inline text-muted-foreground/40">·</span>
            <span data-testid="footer-made-in-india" className="text-[12px] text-accent/90 font-medium tracking-wide">
              Made in India
            </span>
          </div>
          <div className="flex items-center gap-5">
            <Link className="hover:text-foreground" to="/privacy" data-testid="footer-link-privacy">Privacy</Link>
            <Link className="hover:text-foreground" to="/terms" data-testid="footer-link-terms">Terms</Link>
            <Link className="hover:text-foreground" to="/contact" data-testid="footer-link-contact">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
